import { propertyUtil } from "views/Pma/PropertyUtil";

const DISPLAY_FIELDS = [
  { name: "priceRange", label: "Price Range" },
  { name: "rangeSubtitle", label: "Range Subtitle" },
  { name: "homesSold", label: "Homes Sold" },
  { name: "daysToSell", label: "Days to Sell" },
  { name: "beds", label: "Bedrooms" },
  { name: "baths", label: "Bathrooms" },
  { name: "sqft", label: "Living Square Feet" },
  { name: "garage", label: "Garage" },
  { name: "lotSize", label: "Lot Size" },
  { name: "view", label: "View" },
];

const getBathroomDecimals = (baths, halfBaths) => {
  if (`${baths}`.includes(".")) {
    return `${baths} bathrooms`;
  }
  if (!halfBaths || halfBaths !== "0") {
    return `${baths}+ bathrooms`;
  }
  return `${baths}.5 bathrooms`;
};

const getHomeOrCondo = (num, mlsAreaID) => {
  if (mlsAreaID === "SBA-20FCondo") {
    return num === "1" ? "condo has" : "condos have";
  }
  return num === "1" ? "home has" : "homes have";
};

const homeOrProperties = (homesSold, agentData, currentArea) => {
  const { listgenUserID } = agentData || {};
  const { mlsAreaID } = currentArea || {};

  if (mlsAreaID === "SBA-20FCondo") {
    return homesSold === "1" ? "Condo" : "Condos";
  }
  if (
    listgenUserID === "101006" ||
    listgenUserID === "100560" ||
    mlsAreaID === "SBA-20FInv"
  ) {
    return homesSold === "1" ? "Property" : "Properties";
  }
  return homesSold === "1" ? "Home" : "Homes";
};

const formatBathsDisplay = (rawBaths, rawHalfBaths, listgenUserID) => {
  let baths = rawBaths != null ? String(rawBaths) : "";
  let halfBaths = rawHalfBaths != null ? String(rawHalfBaths) : "";

  if (baths === "1.5" && halfBaths === "0") {
    baths = "1";
    halfBaths = "1";
  }

  const useDecimal = listgenUserID === "102340" || baths.includes(".");

  if (useDecimal) {
    return getBathroomDecimals(baths, halfBaths);
  }

  const bathsLabel = `${baths} full ${baths === "1" ? "bath" : "baths"}`;
  if (+halfBaths > 0) {
    return `${bathsLabel} + ${halfBaths} half ${
      halfBaths === "1" ? "bath" : "baths"
    }`;
  }
  return bathsLabel;
};

const getHardcodedSba35jDisplay = (rangeNum) => {
  if (rangeNum === 1) {
    return {
      priceRange: "$620K - $729K",
      rangeSubtitle: "Price Range #1",
      homesSold: "4 Homes Sold",
      daysToSell: "",
      beds: "1 Bedroom",
      baths: "1 Bathroom",
      sqft: "770-848 Living Square Feet",
      garage: "",
      lotSize: "",
      view: "",
    };
  }
  if (rangeNum === 2) {
    return {
      priceRange: "$735K - $925K",
      rangeSubtitle: "Price Range #2",
      homesSold: "6 Homes Sold",
      daysToSell: "",
      beds: "1 Bedroom + 1 Den",
      baths: "1 Bathroom",
      sqft: "945-1063 Living Square Feet",
      garage: "",
      lotSize: "",
      view: "",
    };
  }
  if (rangeNum === 3) {
    return {
      priceRange: "$615K - $935KK",
      rangeSubtitle: "Price Range #3",
      homesSold: "7 Homes Sold",
      daysToSell: "",
      beds: "2 & 3 Bedrooms",
      baths: "2 Bathrooms",
      sqft: "1072-1240 Living Square Feet",
      garage: "",
      lotSize: "",
      view: "",
    };
  }
  return null;
};

export function getRangeDisplayFieldDefs() {
  return DISPLAY_FIELDS;
}

export function getDefaultFieldVisibility() {
  return DISPLAY_FIELDS.reduce((acc, { name }) => {
    acc[name] = true;
    return acc;
  }, {});
}

/** Maps editor field names ↔ priceRangeDashBoardDetailsDTO priceRangeList keys */
const DISPLAY_DTO_FIELD_MAP = {
  priceRange: {
    display: "displayPriceRange",
    original: "originalDisplayPriceRange",
    show: "showPriceRange",
  },
  rangeSubtitle: {
    display: "displaypriceRangeText",
    original: "originalDisplaypriceRangeText",
    show: "showPriceRangeText",
  },
  homesSold: {
    display: "displayHomesSold",
    original: "originalDisplayHomesSold",
    show: "showHomesSold",
  },
  daysToSell: {
    display: "displayDaysToSell",
    original: "originalDisplayDaysToSell",
    show: "showDaysToSell",
  },
  beds: {
    display: "displayBeds",
    original: "originalDisplayBeds",
    show: "showBeds",
  },
  baths: {
    display: "displayBaths",
    original: "originalDisplayBaths",
    show: "showBaths",
  },
  sqft: {
    display: "displaySqft",
    original: "originalDisplaySqft",
    show: "showSqft",
  },
  garage: {
    display: "displayGarage",
    original: "originalDisplayGarage",
    show: "showGarage",
  },
  lotSize: {
    display: "displayLotSize",
    original: "originalDisplayLotSize",
    show: "showLotSize",
  },
  view: {
    display: "displayView",
    original: "originalDisplayView",
    show: "showView",
  },
};

const normalizeDisplayValue = (value) =>
  value == null ? "" : String(value);

/**
 * Read originalDisplay* for an editor field from a DTO priceRangeList item.
 * Returns null when the DTO key is missing (so callers can fall back).
 */
export function getDtoOriginalDisplayValue(dtoItem, fieldName) {
  const keys = DISPLAY_DTO_FIELD_MAP[fieldName];
  if (!dtoItem || !keys || !(keys.original in dtoItem)) {
    return null;
  }
  return normalizeDisplayValue(dtoItem[keys.original]);
}

/**
 * True when any displayOverrides text differs from originalValues
 * (customized vs server originalDisplay*).
 * Empty / null / "0" on both sides count as the same (not edited).
 */
export function isRangeDisplayEdited(range) {
  if (!range?.displayOverrides || !range?.originalValues) {
    return false;
  }
  return DISPLAY_FIELDS.some(({ name }) => {
    if (!(name in range.originalValues)) {
      return false;
    }
    const current = range.displayOverrides[name];
    const original = range.originalValues[name];
    const currentMeaningful = hasDisplayValue(current);
    const originalMeaningful = hasDisplayValue(original);
    if (!currentMeaningful && !originalMeaningful) {
      return false;
    }
    if (currentMeaningful !== originalMeaningful) {
      return true;
    }
    return String(current).trim() !== String(original).trim();
  });
}

/** True when a display string is worth rendering on the dashboard. */
export function hasDisplayValue(value) {
  if (value == null || value === false) {
    return false;
  }
  if (typeof value === "number") {
    return value !== 0 && !Number.isNaN(value);
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    // API sometimes sends "0" as an empty sentinel (e.g. displayView).
    return trimmed !== "" && trimmed !== "0";
  }
  return Boolean(value);
}

/**
 * Force Show=false for any field whose submitted display value is falsy/empty.
 */
export function syncVisibilityWithDisplayValues(displayForm, fieldVisibility) {
  const next = {
    ...getDefaultFieldVisibility(),
    ...(fieldVisibility || {}),
  };
  Object.keys(next).forEach((fieldName) => {
    if (!hasDisplayValue(displayForm?.[fieldName])) {
      next[fieldName] = false;
    }
  });
  return next;
}

/**
 * Convert one priceRangeList item from priceRangeDashBoardDetailsDTO into the
 * editor/render shape used by DashboardBody / DashboardBodyRange.
 */
export function mapDtoRangeItemToEditorRange(item) {
  if (!item) {
    return null;
  }

  const displayOverrides = {};
  const originalValues = {};
  const fieldVisibility = getDefaultFieldVisibility();

  Object.entries(DISPLAY_DTO_FIELD_MAP).forEach(([fieldName, keys]) => {
    displayOverrides[fieldName] = normalizeDisplayValue(item[keys.display]);
    originalValues[fieldName] = normalizeDisplayValue(item[keys.original]);
    if (!hasDisplayValue(displayOverrides[fieldName])) {
      fieldVisibility[fieldName] = false;
    } else if (item[keys.show] !== undefined) {
      fieldVisibility[fieldName] = item[keys.show] !== false;
    }
  });

  return {
    ...item,
    PriceRangeLow: item.priceRangeLow,
    PriceRangeHigh: item.priceRangeHigh,
    AverageBaths: item.averageBaths,
    AverageHalfBaths: item.averageHalfBaths,
    AverageBeds: item.averageBeds,
    AverageDaysOnMarket: item.averageDaysOnMarket,
    AverageGarageSpaces: item.averageGarageSpaces,
    AverageSquareFeet: item.averageSquareFeet,
    AverageLotSize: item.averageLotSize,
    HomesSold: item.homesSold,
    OceanView: item.oceanView,
    golfView: item.golfView,
    displayOverrides,
    originalValues,
    fieldVisibility,
  };
}

/** Area DTO uses priceRangeListArea; city DTO uses priceRangeListCity. */
export function getPriceRangeListKey(useCity) {
  return useCity ? "priceRangeListCity" : "priceRangeListArea";
}

export function getPriceRangeListFromDto(dto, useCity = false) {
  if (!dto) {
    return null;
  }
  const key = getPriceRangeListKey(useCity);
  const list = dto[key];
  return Array.isArray(list) ? list : null;
}

export function clonePriceRangeList(list) {
  if (!Array.isArray(list)) {
    return [];
  }
  return list.map((item) => ({ ...item }));
}

export function buildRangesFromPriceRangeDto(dto, useCity = false) {
  const list = getPriceRangeListFromDto(dto, useCity);
  if (!list || list.length === 0) {
    return null;
  }
  return list.map(mapDtoRangeItemToEditorRange);
}

/**
 * Write editor form values back onto a DTO priceRangeList item (display + show).
 * originalDisplay* values are left unchanged for revert.
 */
export function applyEditorStateToDtoRangeItem(
  dtoItem,
  displayForm,
  fieldVisibility,
) {
  const next = { ...(dtoItem || {}) };
  const syncedVisibility = syncVisibilityWithDisplayValues(
    displayForm,
    fieldVisibility,
  );
  Object.entries(DISPLAY_DTO_FIELD_MAP).forEach(([fieldName, keys]) => {
    next[keys.display] =
      displayForm?.[fieldName] != null ? displayForm[fieldName] : "";
    next[keys.show] = syncedVisibility[fieldName] !== false;
  });
  return next;
}

export function resolveFieldVisibility(rangeOrVisibility) {
  let visibilityMap = null;
  if (rangeOrVisibility?.fieldVisibility) {
    visibilityMap = rangeOrVisibility.fieldVisibility;
  } else if (
    rangeOrVisibility &&
    typeof rangeOrVisibility === "object" &&
    !("AverageBaths" in rangeOrVisibility) &&
    !("displayOverrides" in rangeOrVisibility) &&
    ("priceRange" in rangeOrVisibility || "baths" in rangeOrVisibility)
  ) {
    visibilityMap = rangeOrVisibility;
  }

  return {
    ...getDefaultFieldVisibility(),
    ...(visibilityMap || {}),
  };
}

export function isFieldVisible(fieldVisibility, key) {
  if (!fieldVisibility || fieldVisibility[key] === undefined) {
    return true;
  }
  return fieldVisibility[key] !== false;
}

/**
 * Build the on-screen display strings for a price-range section.
 * Prefers saved displayOverrides when present.
 */
export function buildRangeDisplayForm(
  range,
  { agentData, currentArea, rangeNum },
) {
  if (!range) {
    return null;
  }

  if (range.displayOverrides) {
    return {
      priceRange: "",
      rangeSubtitle: `Price Range #${rangeNum}`,
      homesSold: "",
      daysToSell: "",
      beds: "",
      baths: "",
      sqft: "",
      garage: "",
      lotSize: "",
      view: "",
      ...range.displayOverrides,
    };
  }

  const { listgenUserID } = agentData || {};
  const { mlsAreaID } = currentArea || {};
  const hardcoded = getHardcodedSba35jDisplay(
    mlsAreaID === "SBA-35J" ? rangeNum : null,
  );
  if (hardcoded) {
    return hardcoded;
  }

  const { roundSalePriceTenths, acreOrSqft, toAcre } = propertyUtil;
  const priceRangeLow = range.PriceRangeLow;
  const priceRangeHigh = range.PriceRangeHigh;
  const homesSold = range.HomesSold != null ? String(range.HomesSold) : "";
  const dom =
    range.AverageDaysOnMarket != null
      ? String(range.AverageDaysOnMarket)
      : "";
  const beds = range.AverageBeds != null ? String(range.AverageBeds) : "";
  const sqft = range.AverageSquareFeet;
  const garageSpaces = range.AverageGarageSpaces;
  const lotSize = range.AverageLotSize;
  const oceanView = range.OceanView;
  const golfView = range.golfView;

  const specialVaitGarage = mlsAreaID === "SBA-20FRes" && rangeNum === 3;
  const useLotPlus = mlsAreaID === "SCL-14U";
  const bayOrOcean = listgenUserID === "104835" ? "bay" : "ocean";
  const acreConvert = toAcre(lotSize, true);
  const useAcreOrSqft = acreOrSqft(lotSize, true);

  const priceRange = `${roundSalePriceTenths(priceRangeLow)}${
    priceRangeHigh == "100000000"
      ? " & up"
      : ` - ${roundSalePriceTenths(priceRangeHigh)}`
  }`;

  const homesSoldText = `${homesSold} ${homeOrProperties(
    homesSold,
    agentData,
    currentArea,
  )} Sold`;

  const daysToSell =
    listgenUserID === "110007"
      ? ""
      : `${dom} ${dom === "1" ? "Day" : "Days"} to Sell`;

  const isSba45hRange3 = mlsAreaID === "SBA-45H" && rangeNum === 3;

  let bedsText = "";
  let bathsText = "";
  let sqftText = "";
  let garageText = "";
  let lotSizeText = "";
  let viewText = "";

  if (isSba45hRange3) {
    if (lotSize && +lotSize >= 1 && mlsAreaID !== "SBA-20FCondo") {
      lotSizeText = `Lot Size: ${acreConvert} ${useAcreOrSqft}${
        useLotPlus ? "+" : ""
      }`;
    }
  } else {
    bedsText = `${beds} ${beds === "1" ? "bedroom" : "bedrooms"}`;
    bathsText = formatBathsDisplay(
      range.AverageBaths,
      range.AverageHalfBaths,
      listgenUserID,
    );

    sqftText = `${Number(sqft).toLocaleString()}${
      listgenUserID === "102340" || listgenUserID === "102356" ? "+" : ""
    } Living Square Feet`;

    if (specialVaitGarage) {
      garageText = "No Garage";
    } else if (
      garageSpaces &&
      +garageSpaces > 0 &&
      mlsAreaID !== "SCL-13A"
    ) {
      garageText =
        listgenUserID === "107348" ? "Garage" : `${garageSpaces} car garage`;
    }

    if (
      lotSize &&
      +lotSize >= 1 &&
      mlsAreaID !== "SBA-20FCondo" &&
      mlsAreaID !== "SCL-13A" &&
      listgenUserID !== "110007"
    ) {
      lotSizeText = `Lot Size: ${acreConvert} ${useAcreOrSqft}${
        useLotPlus ? "+" : ""
      }`;
    }

    if (+oceanView > 0) {
      viewText = `${oceanView} ${getHomeOrCondo(
        String(oceanView),
        mlsAreaID,
      )} an ${bayOrOcean} view`;
    } else if (+golfView > 0) {
      viewText = `${golfView} ${getHomeOrCondo(
        String(golfView),
        mlsAreaID,
      )} a golf course view`;
    }
  }

  return {
    priceRange,
    rangeSubtitle: `Price Range #${rangeNum}`,
    homesSold: homesSoldText,
    daysToSell,
    beds: bedsText,
    baths: bathsText,
    sqft: sqftText,
    garage: garageText,
    lotSize: lotSizeText,
    view: viewText,
  };
}

export function buildRangeEditorState(
  range,
  { agentData, currentArea, rangeNum },
) {
  // Prefer DTO-backed originals/display when present (priceRangeDashBoardDetailsDTO).
  if (range?.displayOverrides && range?.originalValues) {
    return {
      displayOverrides: { ...range.displayOverrides },
      originalValues: { ...range.originalValues },
      fieldVisibility: resolveFieldVisibility(range),
    };
  }

  const opts = { agentData, currentArea, rangeNum };
  const rangeWithoutOverrides = { ...(range || {}) };
  delete rangeWithoutOverrides.displayOverrides;

  return {
    displayOverrides: buildRangeDisplayForm(range, opts),
    originalValues: buildRangeDisplayForm(rangeWithoutOverrides, opts),
    fieldVisibility: resolveFieldVisibility(range),
  };
}

export function applyDisplayOverridesToRange(
  range,
  displayForm,
  fieldVisibility,
) {
  const syncedVisibility = syncVisibilityWithDisplayValues(
    displayForm,
    fieldVisibility,
  );
  return {
    ...range,
    displayOverrides: {
      priceRange: displayForm.priceRange || "",
      rangeSubtitle: displayForm.rangeSubtitle || "",
      homesSold: displayForm.homesSold || "",
      daysToSell: displayForm.daysToSell || "",
      beds: displayForm.beds || "",
      baths: displayForm.baths || "",
      sqft: displayForm.sqft || "",
      garage: displayForm.garage || "",
      lotSize: displayForm.lotSize || "",
      view: displayForm.view || "",
    },
    originalValues: range.originalValues
      ? { ...range.originalValues }
      : range.originalValues,
    fieldVisibility: syncedVisibility,
  };
}
